"use client";

import { Check, Repeat2 } from "lucide-react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../../shadcn/ui/drawer";
import { useMemo, useState } from "react";
import { usePathname, useRouter } from "next/navigation";

import { Comedian } from "@punchup/data";
import Image from "next/image";
import { SearchInput } from "../../shadcn/ui/search-input";
import { Skeleton } from "../../shadcn/ui/skeleton";
import { cn, getCDNImageURL } from "@punchup/utils";

interface ComedianSelectorDrawerProps {
  comedians: Comedian[];
  selectedComedian: Comedian | null;
  isLoading: boolean;
  isSuperAdmin?: boolean;
}

export function ComedianSelectorDrawer({
  comedians,
  selectedComedian,
  isLoading,
  isSuperAdmin,
}: ComedianSelectorDrawerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const router = useRouter();
  const path = usePathname();

  const currentPath =
    isLoading || !selectedComedian?.slug
      ? ""
      : path
          .split(`/${selectedComedian.slug}/`)
          .filter((p) => p !== "")
          .pop();

  const sortedComedians = useMemo(() => {
    return [...comedians].sort((a, b) =>
      a.display_name.toLowerCase().localeCompare(b.display_name.toLowerCase())
    );
  }, [comedians]);

  const filteredComedians = useMemo(() => {
    if (!search.trim()) return sortedComedians;
    const query = search.toLowerCase();
    return sortedComedians.filter((c) =>
      c.display_name.toLowerCase().includes(query)
    );
  }, [sortedComedians, search]);

  const onlyOneComedian = comedians.length === 1;

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger
        asChild
        disabled={isLoading || (onlyOneComedian && !isSuperAdmin)}
      >
        <button
          className={cn(
            "flex flex-row items-center gap-1.5 p-1 rounded-lg bg-transparent hover:bg-neutral-200/50 transition-colors duration-200 ease-in-out min-w-0",
            onlyOneComedian && !isSuperAdmin && "cursor-default"
          )}
          disabled={isLoading || (onlyOneComedian && !isSuperAdmin)}
        >
          {isLoading ? (
            <Skeleton className="w-7 h-7 bg-neutral-200 shrink-0" />
          ) : selectedComedian?.about_img ? (
            <div className="w-7 h-7 aspect-square bg-black flex flex-col items-center justify-center overflow-hidden rounded-full shrink-0">
              <Image
                src={getCDNImageURL(selectedComedian.about_img)}
                alt={selectedComedian.display_name}
                width={100}
                height={100}
                className="h-full w-full aspect-square object-cover"
              />
            </div>
          ) : (
            <div className="w-7 h-7 rounded-lg bg-neutral-200 border border-neutral-300 shrink-0" />
          )}
          <div className="flex flex-col items-start gap-[1px] leading-tight select-none shrink min-w-0 truncate overflow-ellipsis text-[12px]">
            {isLoading ? (
              <Skeleton className="mt-[2px] bg-neutral-200 w-16 h-[14px]" />
            ) : (
              <p className="text-left w-full truncate overflow-ellipsis">
                {selectedComedian?.display_name}
              </p>
            )}
          </div>
          {onlyOneComedian && !isSuperAdmin ? null : (
            <Repeat2 size={14} className="shrink-0" />
          )}
        </button>
      </DrawerTrigger>
      <DrawerContent className="h-[85dvh] pb-safe bg-white">
        <DrawerHeader>
          <DrawerTitle className="sr-only">Switch comedian</DrawerTitle>
        </DrawerHeader>
        {isSuperAdmin && (
          <div className="px-4">
            <SearchInput
              placeholder="Search comedians..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {filteredComedians.map((comedian) => (
            <DrawerClose asChild key={comedian.id}>
              <button
                className="cursor-pointer flex flex-row items-center gap-2.5 p-2 w-full text-primary-blue rounded-lg transition-colors duration-200 ease-in-out hover:bg-neutral-200/50"
                onClick={() => {
                  router.push(`/${comedian.slug}/${currentPath}`);
                  setOpen(false);
                }}
              >
                {comedian.about_img ? (
                  <div className="w-11 h-11 aspect-square bg-black flex flex-col items-center justify-center overflow-hidden rounded-full shrink-0">
                    <Image
                      src={getCDNImageURL(comedian.about_img)}
                      alt={comedian.display_name}
                      width={100}
                      height={100}
                      className="h-full w-full aspect-square object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-11 h-11 rounded-lg bg-neutral-200 border border-neutral-300 shrink-0" />
                )}
                <div className="text-[14px] flex flex-col items-start gap-[1px] leading-tight select-none flex-1 min-w-0">
                  <p className="text-left w-full truncate">
                    {comedian.display_name}
                  </p>
                </div>
                {comedian.id === selectedComedian?.id && (
                  <Check size={16} className="ml-auto shrink-0" />
                )}
              </button>
            </DrawerClose>
          ))}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
